import DataTable, { ColumnAlignment } from '@/common/tableData/DataTable';
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { FormGroup, Input, Label } from 'reactstrap';
import { useEffect, useState } from 'react';
import DebouncedInput from '@/components/DebounceInput';
import { ColumnFilter, PaginationState, SortingState } from '@tanstack/react-table';
import useTournamentTemplates from '@/hooks/useTournamentTemplates';

const SngTemplateTable = ({
  onDeleteTemplate,
  onSelectTemplate,
}: {
  onDeleteTemplate: (templateId: string) => void;
  onSelectTemplate: (templateId: string) => void;
}) => {
  const { tournamentTemplates, refetchTournamentTemplates } = useTournamentTemplates({ sng: true });

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'name',
      desc: true,
    },
  ]);

  useEffect(() => {
    refetchTournamentTemplates();
  }, []);

  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([
    {
      id: 'includeSnG',
      value: true,
    },
    {
      id: 'includeSatellites',
      value: true,
    },
  ]);

  const toggleColumnFilter = (filterId: string) => {
    setColumnFilters((filters) => {
      const newFilters = filters.filter((filter) => filter.id !== filterId);
      return [...newFilters, { id: filterId, value: !filters.find((filter) => filter.id === filterId)?.value }];
    });
  };

  const handleTemplateNameSearch = (value: string) => {
    setColumnFilters((filters) => {
      const newFilters = filters.filter((filter) => filter.id !== 'name');
      const existingNameFilter = filters.find((filter) => filter.id === 'name');
      if (existingNameFilter && value !== '') {
        return [
          ...newFilters,
          {
            id: 'name',
            value: value,
          },
        ];
      }

      if (existingNameFilter && value === '') {
        return newFilters;
      }

      if (!existingNameFilter && value !== '') {
        return [
          ...newFilters,
          {
            id: 'name',
            value: value,
          },
        ];
      }

      return filters;
    });
  };

  if (!tournamentTemplates) {
    return null;
  }

  return (
    <div className="d-flex flex-column gap-3 py-2">
      <div className="w-100 d-flex justify-content-between align-items-center py-2">
        <DebouncedInput
          className="form-control w-auto"
          placeholder="Search"
          isLoading={false}
          onChange={(val) => handleTemplateNameSearch(val as string)}
          value={columnFilters.find((filter) => filter.id === 'name')?.value as string}
        />
      </div>
      <DataTable
        data={tournamentTemplates}
        columnFilters={columnFilters}
        sorting={sorting}
        onSortingChange={setSorting}
        columns={[
          {
            header: 'Name',
            accessorKey: 'name',
            size: 600,
          },
          {
            header: 'Game',
            accessorKey: 'game',
            size: 120,
          },
          {
            header: 'Players',
            accessorKey: 'minPlayers',
            size: 120,
            meta: {
              alignment: ColumnAlignment.RIGHT,
            },
          },
          {
            header: 'Actions',
            cell: ({ row }) => (
              <div className="d-flex justify-content-center flex-row gap-2">
                <div className="d-flex justify-content-start w-full flex-row">
                  <AiOutlineDelete
                    className="cursor-pointer"
                    onClick={() => onDeleteTemplate(row.original.id)}
                    size={20}
                  />
                </div>
                <div className="d-flex justify-content-start w-full flex-row">
                  <AiOutlineEdit
                    className="cursor-pointer"
                    onClick={() => onSelectTemplate(row.original.id)}
                    size={20}
                  />
                </div>
              </div>
            ),
            meta: {
              alignment: ColumnAlignment.CENTER,
            },
          },
        ]}
      />
    </div>
  );
};

export default SngTemplateTable;
